// src/oura/newscan/businessSeen.ts
//
// First AI check: whether staff have looked over the Business step for this
// draft and pressed "Looks right". The draft exists before staff open the
// flow, so without this the flow opened on Review and the track ticked
// Business that nobody had checked (Jovi, 20 Oct).
//
// Browser only, per draft: the server has no field for it yet. Absent (a new
// browser, cleared storage) reads as "not seen", so the worst case is that
// Business opens first once more.

import { useCallback, useSyncExternalStore } from "react";

const KEY = (draftId: string) => `oura.scanDraft.businessSeen.${draftId}`;
const listeners = new Set<() => void>();

function read(draftId: string | null): boolean {
    if (!draftId) return false;
    try {
        return window.localStorage.getItem(KEY(draftId)) === "1";
    } catch {
        return false;
    }
}

export function markBusinessSeen(draftId: string | null): void {
    if (!draftId || read(draftId)) return;
    try {
        window.localStorage.setItem(KEY(draftId), "1");
    } catch {
        // Storage refused: the step still moves on, it only asks again next time.
    }
    listeners.forEach((l) => l());
}

export function useBusinessSeen(draftId: string | null): [boolean, () => void] {
    const seen = useSyncExternalStore(
        (l) => {
            listeners.add(l);
            return () => listeners.delete(l);
        },
        () => read(draftId),
        () => false,
    );
    const mark = useCallback(() => markBusinessSeen(draftId), [draftId]);
    return [seen, mark];
}
